/**
 * Politica CENTRAL de clasificacion de cambios (requisito §63).
 * Unico lugar donde viven estos umbrales: docs/TRAINING_ENGINE.md §10 y
 * docs/NUTRITION_ENGINE.md §8 los citan, no los duplican.
 */

export type ChangeKind =
  | 'next_set_load'
  | 'session_load'
  | 'macro_redistribution'
  | 'equivalent_substitution'
  | 'week_reorganization'
  | 'weekly_volume'
  | 'energy_target'
  | 'phase_change'
  | 'split_change'
  | 'unplanned_deload'
  | 'priority_change'
  | 'block_duration'
  | 'stall_response'
  | 'plan_kept';

export type ChangeLevel = 'minor' | 'relevant';

export const CHANGE_THRESHOLDS = {
  /** Ajuste de carga dentro de la sesion: siempre menor (se corrige en la serie siguiente). */
  nextSetLoadPct: 0.1,
  /** Cambio de carga entre sesiones: coincide con el limite duro. */
  sessionLoadPct: 0.1,
  /** Variacion de volumen semanal respecto al inicio del bloque. */
  weeklyVolumePct: 0.2,
  /** Cambio del objetivo energetico respecto al vigente (ADR-018). */
  energyTargetPct: 0.08,
  /** Redistribucion de macros dentro del mismo objetivo diario. */
  macroRedistributionKcalPct: 0.03,
  /** Similitud minima para que una sustitucion sea operativa. */
  substitutionSimilarity: 0.7,
  /** Reorganizacion semanal sin tocar el volumen total mas de esto. */
  weekReorganizationVolumePct: 0.1,
  /**
   * Cambio de duracion del bloque (docs/TRAINING_ENGINE.md §2.2).
   *
   * A diferencia del resto, este umbral esta en SEMANAS, no en tanto por uno:
   * alargar un bloque una semana es lo mismo tenga la duracion que tenga, y
   * expresarlo en porcentaje haria que la misma semana fuera menor en un bloque
   * de ocho y relevante en uno de cuatro.
   */
  blockDurationWeeks: 1,
  /**
   * Escalon de respuesta al estancamiento (docs/TRAINING_ENGINE.md §9).
   *
   * Como `blockDurationWeeks`, este umbral NO es un tanto por uno: es la
   * posicion en la escalera del §9. Los escalones 2, 3 y 4 —cambiar de
   * estrategia, reduccion tecnica, sustituir el ejercicio— son ajustes menores
   * porque ninguno cambia lo que se entrena. El 5 si: evaluar deload, cambio de
   * fase o revision nutricional es una decision del usuario.
   */
  stallResponseStep: 4,
} as const;

export interface ChangeInput {
  kind: ChangeKind;
  /** Magnitud relativa del cambio (0,05 = 5 %). Opcional segun el tipo. */
  magnitude?: number;
  /** Similitud, solo para sustituciones. */
  similarity?: number;
}

export interface ChangeClassification {
  level: ChangeLevel;
  requiresApproval: boolean;
  reasonCodes: string[];
}

export function classifyChange(input: ChangeInput): ChangeClassification {
  const magnitude = Math.abs(input.magnitude ?? 0);

  switch (input.kind) {
    // Cambios que SIEMPRE requieren aprobacion del usuario.
    case 'phase_change':
    case 'split_change':
    case 'unplanned_deload':
    case 'priority_change':
      return relevant(`${input.kind}_always_relevant`);
    case 'next_set_load':
      return minor('within_next_set_band');
    case 'plan_kept':
      // Mantener el plan no mueve nada, asi que no hay nada que aprobar. Pasa
      // por aqui igual que el resto para que la regla de quien pregunta siga
      // viviendo en un solo sitio (training/review.ts).
      return minor('no_change_to_approve');
    case 'session_load':
      return magnitude <= CHANGE_THRESHOLDS.sessionLoadPct
        ? minor('within_session_load_band')
        : relevant('session_load_above_band');
    case 'macro_redistribution':
      return magnitude <= CHANGE_THRESHOLDS.macroRedistributionKcalPct
        ? minor('same_daily_target')
        : relevant('macro_change_alters_target');
    case 'equivalent_substitution':
      return (input.similarity ?? 0) >= CHANGE_THRESHOLDS.substitutionSimilarity
        ? minor('equivalent_stimulus')
        : relevant('substitution_changes_stimulus');
    case 'week_reorganization':
      return magnitude <= CHANGE_THRESHOLDS.weekReorganizationVolumePct
        ? minor('reorder_only')
        : relevant('reorganization_changes_volume');
    case 'stall_response':
      // `magnitude` viene como escalon de la escalera del §9.
      return magnitude <= CHANGE_THRESHOLDS.stallResponseStep
        ? minor('stall_response_within_ladder')
        : relevant('stall_needs_structural_review');
    case 'block_duration':
      // `magnitude` viene en semanas para este tipo. Ver CHANGE_THRESHOLDS.
      return magnitude <= CHANGE_THRESHOLDS.blockDurationWeeks
        ? minor('duration_within_one_week')
        : relevant('duration_change_above_one_week');
    case 'weekly_volume':
      return magnitude <= CHANGE_THRESHOLDS.weeklyVolumePct
        ? minor('within_volume_band')
        : relevant('volume_above_band');
    case 'energy_target':
      return magnitude <= CHANGE_THRESHOLDS.energyTargetPct
        ? minor('within_measurement_noise')
        : relevant('energy_target_above_band');
    default: {
      const exhaustive: never = input.kind;
      throw new Error(`Tipo de cambio no soportado: ${String(exhaustive)}`);
    }
  }
}

function minor(code: string): ChangeClassification {
  return { level: 'minor', requiresApproval: false, reasonCodes: [code] };
}

function relevant(code: string): ChangeClassification {
  return { level: 'relevant', requiresApproval: true, reasonCodes: [code] };
}

/** Los cambios relevantes pendientes caducan a los 7 dias (docs/TRAINING_ENGINE.md §10). */
export const APPROVAL_EXPIRY_DAYS = 7;
